import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ChecklistItem,
  ChecklistStatus,
  ChecklistSubtask,
  parseSubtasks,
  serializeSubtasks,
  UPLOADABLE_SUBTASK_KEYS
} from 'src/interfaces/ChecklistTypes';
import { AdminUser } from 'src/interfaces/TicketTypes';
import SubtaskDocumentUpload from './SubtaskDocumentUpload';

type EmailIconState = 'idle' | 'loading' | 'success' | 'error';

interface Props {
  item: ChecklistItem;
  installationId: number;
  adminUsers: AdminUser[];
  onUpdate: (
    id: number,
    patch: Partial<{
      status: number;
      comments: string;
      assigneeId: number | null;
      doneAt: string | null;
      subtasks: string;
    }>
  ) => Promise<boolean>;
  onNotify: (id: number) => Promise<boolean>;
}

const statusColors: Record<number, string> = {
  [ChecklistStatus.NotStarted]: '#9e9e9e',
  [ChecklistStatus.InProgress]: '#ed6c02',
  [ChecklistStatus.Done]: '#2e7d32'
};

function ChecklistStepRow({ item, installationId, adminUsers, onUpdate, onNotify }: Props) {
  const intl = useIntl();
  const [comments, setComments] = useState(item.comments ?? '');
  const [emailState, setEmailState] = useState<EmailIconState>('idle');
  const [subtasksOpen, setSubtasksOpen] = useState(false);
  const [subtasks, setSubtasks] = useState<ChecklistSubtask[]>(() =>
    parseSubtasks(item.subtasks)
  );

  useEffect(() => {
    setComments(item.comments ?? '');
  }, [item.comments]);

  useEffect(() => {
    setSubtasks(parseSubtasks(item.subtasks));
  }, [item.subtasks]);

  const subtaskSummary = useMemo(() => {
    if (subtasks.length === 0) return null;
    const done = subtasks.filter((s) => s.checked).length;
    return `${done}/${subtasks.length}`;
  }, [subtasks]);

  const handleStatusChange = async (value: number) => {
    await onUpdate(item.id, { status: value });
  };

  const handleAssigneeChange = async (rawValue: string) => {
    if (rawValue === '') {
      await onUpdate(item.id, { assigneeId: null });
    } else {
      await onUpdate(item.id, { assigneeId: Number(rawValue) });
    }
  };

  const handleDoneAtChange = async (value: string) => {
    await onUpdate(item.id, { doneAt: value || null });
  };

  const handleCommentsBlur = async () => {
    if (comments !== (item.comments ?? '')) {
      await onUpdate(item.id, { comments });
    }
  };

  const handleSubtaskToggle = async (index: number) => {
    const updated = subtasks.map((s, i) =>
      i === index ? { ...s, checked: !s.checked } : s
    );
    setSubtasks(updated);
    await onUpdate(item.id, { subtasks: serializeSubtasks(updated) });
  };

  const handleNotifyClick = async () => {
    if (!item.assigneeId || emailState === 'loading') return;
    setEmailState('loading');
    const ok = await onNotify(item.id);
    setEmailState(ok ? 'success' : 'error');
    setTimeout(() => setEmailState('idle'), 2500);
  };

  const renderEmailIcon = () => {
    if (emailState === 'success') return <CheckIcon sx={{ color: '#2e7d32' }} />;
    if (emailState === 'error') return <CloseIcon sx={{ color: '#d32f2f' }} />;
    return <EmailIcon />;
  };

  const filteredAdmins = useMemo(
    () =>
      adminUsers.filter((u) => {
        const name = (u.name ?? '').toLowerCase();
        return (
          !name.includes('inesco energy master admin') &&
          !name.includes('paal myhre')
        );
      }),
    [adminUsers]
  );

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 48, verticalAlign: 'top' }}>
          <Typography variant="body2" fontWeight={600}>
            {item.stepNumber}
          </Typography>
        </TableCell>

        <TableCell sx={{ verticalAlign: 'top', minWidth: 240 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">
              {intl.formatMessage({
                id: `checklistStep${item.stepNumber}`,
                defaultMessage: item.stepTitle
              })}
            </Typography>
            {subtasks.length > 0 && (
              <>
                <Chip
                  size="small"
                  label={subtaskSummary}
                  color={
                    subtasks.every((s) => s.checked) ? 'success' : 'default'
                  }
                />
                <IconButton
                  size="small"
                  onClick={() => setSubtasksOpen((o) => !o)}
                  aria-label={intl.formatMessage({
                    id: 'checklistToggleSubtasks',
                    defaultMessage: 'Toggle subtasks'
                  })}
                >
                  {subtasksOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </>
            )}
          </Stack>
        </TableCell>

        <TableCell sx={{ verticalAlign: 'top', width: 160 }}>
          <FormControl size="small" fullWidth>
            <Select
              value={item.status}
              onChange={(e) => handleStatusChange(Number(e.target.value))}
              sx={{
                '& .MuiSelect-select': {
                  color: statusColors[item.status] ?? 'inherit',
                  fontWeight: 600
                }
              }}
            >
              <MenuItem value={ChecklistStatus.NotStarted}>
                <FormattedMessage id="checklistNotStarted" defaultMessage="Not Started" />
              </MenuItem>
              <MenuItem value={ChecklistStatus.InProgress}>
                <FormattedMessage id="checklistInProgress" defaultMessage="In Progress" />
              </MenuItem>
              <MenuItem value={ChecklistStatus.Done}>
                <FormattedMessage id="checklistDone" defaultMessage="Done" />
              </MenuItem>
            </Select>
          </FormControl>
        </TableCell>

        <TableCell sx={{ verticalAlign: 'top', width: 260 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={item.assigneeId ?? ''}
                displayEmpty
                onChange={(e) => handleAssigneeChange(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>
                    <FormattedMessage
                      id="checklistNoAssignee"
                      defaultMessage="Unassigned"
                    />
                  </em>
                </MenuItem>
                {filteredAdmins.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip
              title={
                !item.assigneeId
                  ? intl.formatMessage({
                      id: 'checklistNotifyDisabledTooltip',
                      defaultMessage: 'Assign someone first to send a notification'
                    })
                  : intl.formatMessage({
                      id: 'checklistNotifyTooltip',
                      defaultMessage: 'Send email notification to assignee'
                    })
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!item.assigneeId || emailState === 'loading'}
                  onClick={handleNotifyClick}
                >
                  {renderEmailIcon()}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>

        <TableCell sx={{ verticalAlign: 'top', width: 160 }}>
          <TextField
            size="small"
            type="date"
            value={item.doneAt ?? ''}
            onChange={(e) => handleDoneAtChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </TableCell>

        <TableCell sx={{ verticalAlign: 'top', minWidth: 280 }}>
          <TextField
            size="small"
            multiline
            minRows={1}
            maxRows={6}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            onBlur={handleCommentsBlur}
            fullWidth
            placeholder={intl.formatMessage({
              id: 'checklistCommentsPlaceholder',
              defaultMessage: 'Notes, contact info, observations…'
            })}
          />
        </TableCell>
      </TableRow>

      {subtasks.length > 0 && subtasksOpen && (
        <TableRow>
          <TableCell colSpan={6} sx={{ backgroundColor: '#fafafa', py: 1 }}>
            <Box pl={6} display="flex" flexDirection="column">
              {subtasks.map((s, i) => (
                <Box key={i}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={s.checked}
                        onChange={() => handleSubtaskToggle(i)}
                      />
                    }
                    label={
                      <Typography variant="body2" component="span">
                        {intl.formatMessage({ id: s.text, defaultMessage: s.text })}
                      </Typography>
                    }
                    sx={{ ml: 0 }}
                  />
                  {UPLOADABLE_SUBTASK_KEYS.has(s.text) && (
                    <SubtaskDocumentUpload
                      installationId={installationId}
                      checklistItemId={item.id}
                      subtaskKey={s.text}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default ChecklistStepRow;
